/**
 * MapSnapshotProvider — the Terrain plane's data source (MERIDIAN).
 *
 * Holds the frozen Search-map snapshot and its sampled environment signals, and
 * writes the ambient tokens (--map-tint / --map-tint-strength / --map-luma /
 * --env-temperature) onto <html> so the bezel + terrain materials pick them up.
 *
 * Sampling is a discrete, one-shot event ("freeze"), NOT a loop — the terrain is
 * static while the user works (see docs/design/MERIDIAN.md § Environmental
 * Context System). This provider owns the *contract*; the actual pixel capture
 * from the live MapLibre canvas is wired in a later task via `freeze()`. Until
 * then the app runs with neutral defaults: no snapshot, no tint, a calm terrain
 * field. Everything downstream is designed to read correctly in that state.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

/** Sampled environment signals, produced once at freeze. All optional-safe. */
export interface EnvSignal {
  /** Blurred data-URL / object-URL of the last live map frame. null = no snapshot yet. */
  snapshot: string | null;
  /** Dominant sampled hue as a CSS color, pre-attenuated to stay under the tint cap. */
  tint: string;
  /** 0..1 saturation of the sample → weights how far tint/tick shift. */
  tintStrength: number;
  /** 0..1 average luminance → drives the luminance-adaptive terrain scrim. */
  luma: number;
  /** -1 (cool) .. +1 (warm), from biome/weather/time metadata. */
  temperature: number;
}

/** The neutral, unsampled state — matches the CSS token defaults exactly. */
const NEUTRAL: EnvSignal = {
  snapshot: null,
  tint: 'transparent',
  tintStrength: 0,
  luma: 0.5,
  temperature: 0,
};

interface MapSnapshotContextValue extends EnvSignal {
  /**
   * Capture the current environment. Called when the live map "sets" (leaving
   * Search). Partial input is merged over the current signal; omitted fields
   * keep their value. Passing nothing is a no-op-safe reset hook for tests.
   */
  freeze: (next: Partial<EnvSignal>) => void;
  /** Reset to the neutral/unsampled state (used by Calm mode and on sign-out). */
  thaw: () => void;
}

const MapSnapshotContext = createContext<MapSnapshotContextValue | null>(null);

/**
 * Push the sampled signals onto <html> as custom properties. The bezel/terrain
 * material tokens (transitions.css) consume these; when they equal the defaults
 * the chrome renders as untinted glass, so this is safe to run with NEUTRAL.
 */
function applyEnvTokens(signal: EnvSignal) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.style.setProperty('--map-tint', signal.tint);
  root.style.setProperty('--map-tint-strength', String(signal.tintStrength));
  root.style.setProperty('--map-luma', String(signal.luma));
  root.style.setProperty('--env-temperature', String(signal.temperature));
}

export const MapSnapshotProvider = ({ children }: { children: ReactNode }) => {
  const [signal, setSignal] = useState<EnvSignal>(NEUTRAL);
  // Guard against writing tokens redundantly on every render.
  const lastApplied = useRef<string>('');

  useEffect(() => {
    const key = `${signal.tint}|${signal.tintStrength}|${signal.luma}|${signal.temperature}`;
    if (key === lastApplied.current) return;
    lastApplied.current = key;
    applyEnvTokens(signal);
  }, [signal]);

  const freeze = useCallback((next: Partial<EnvSignal>) => {
    setSignal((prev) => ({ ...prev, ...next }));
  }, []);

  const thaw = useCallback(() => setSignal(NEUTRAL), []);

  const value = useMemo<MapSnapshotContextValue>(
    () => ({ ...signal, freeze, thaw }),
    [signal, freeze, thaw]
  );

  return (
    <MapSnapshotContext.Provider value={value}>
      {children}
    </MapSnapshotContext.Provider>
  );
};

/**
 * Access the frozen-map snapshot + environment signals. Safe outside a provider
 * (returns the neutral state with no-op freeze/thaw), so components — and tests —
 * never need to special-case its absence.
 */
export function useMapSnapshot(): MapSnapshotContextValue {
  const ctx = useContext(MapSnapshotContext);
  if (ctx) return ctx;
  return { ...NEUTRAL, freeze: () => {}, thaw: () => {} };
}
